import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';
import { getStore } from '@theme/it-pdp-store';

/**
 * IT PDP — gallery.
 *
 * Four jobs, all local to this block:
 *
 *   1. Show the plates shot in the chosen colour. Every plate carries the
 *      colour it belongs to; plates with none are shared and always show.
 *   2. Pin the wall on its last row. If the wall is taller than the
 *      viewport the sticky offset goes negative, so it travels up with
 *      the page and only sticks once its foot lands on the viewport
 *      floor. CSS cannot know that height, so we measure it.
 *   3. Page the rail one plate at a time below 920, retiring the
 *      chevrons at either end.
 *   4. Drive the lightbox.
 */
class ItPdpGallery extends Component {
  requiredRefs = ['wall'];

  #onResize = () => {
    this.#syncWall();
    this.#syncRailEnds();
  };

  #lightboxIndex = 0;

  connectedCallback() {
    super.connectedCallback();

    const { wall, railPrev, railNext, shareButton } = this.refs;

    this.#allCells = Array.from(wall.querySelectorAll('.it-gallery__cell'));
    this.#cells = this.#allCells;

    // Liquid emits comma-separated pairs and always leaves a trailing comma;
    // wrapping and trimming it here beats contorting the template.
    try {
      const pairs = (wall.dataset.groupColours || '').trim().replace(/,$/, '');
      this.#groupColours = pairs ? JSON.parse(`{${pairs}}`) : {};
    } catch {
      this.#groupColours = {};
    }

    const store = getStore(this);
    const colourOption = wall.dataset.colourOption;

    if (store && colourOption && Object.keys(this.#groupColours).length) {
      // Before a colour is chosen the wall still has to be coherent, so it
      // opens on the colourway the server featured rather than a mix of all
      // ten. Choosing one then swaps it.
      const index = store.options.findIndex((option) => option.name === colourOption);
      const fallback = index === -1 ? null : store.defaultVariant?.options[index];

      this.#unsubscribe = store.subscribe(({ selections }) =>
        this.#showColour(selections[colourOption] ?? fallback)
      );
    } else {
      this.#applyLimit(this.#allCells);
    }

    wall.addEventListener('scroll', this.#syncRailEnds, { passive: true });
    railPrev?.addEventListener('click', () => this.#railStep(-1));
    railNext?.addEventListener('click', () => this.#railStep(1));

    shareButton?.addEventListener('click', this.#share);

    if (this.refs.lightbox) {
      wall.addEventListener('click', this.#openFromCell);
      this.refs.lightboxClose?.addEventListener('click', this.#closeLightbox);
      this.refs.lightboxPrev?.addEventListener('click', () => this.#showShot(this.#lightboxIndex - 1));
      this.refs.lightboxNext?.addEventListener('click', () => this.#showShot(this.#lightboxIndex + 1));
      this.refs.lightbox.addEventListener('click', (event) => {
        if (event.target === this.refs.lightbox) this.#closeLightbox();
      });
      document.addEventListener('keydown', this.#onKeydown);
    }

    window.addEventListener('resize', this.#onResize, { passive: true });
    window.addEventListener('load', this.#syncWall, { once: true });

    // Images and webfonts both change the wall's height after first paint.
    document.fonts?.ready.then(this.#syncWall).catch(() => {});
    for (const img of wall.querySelectorAll('img')) {
      if (!img.complete) img.addEventListener('load', this.#syncWall, { once: true });
    }

    this.#syncWall();
    this.#syncRailEnds();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#onResize);
    document.removeEventListener('keydown', this.#onKeydown);
    // A section swap while the lightbox is open would otherwise leave the
    // page locked with nothing on screen to close.
    unlockScroll(this);
    this.#unsubscribe?.();
  }

  /** @type {HTMLElement[]} */
  #cells = [];

  /** @type {HTMLElement[]} */
  #allCells = [];

  #unsubscribe;

  /** @type {Record<string, string>} group id -> colour value */
  #groupColours = {};

  /* ---------- colour filtering ---------- */

  #showColour(colour) {
    // Once a product tags its shots, the wall is fully partitioned: an
    // untagged plate is an orphan from the catalogue (a retired colourway
    // whose images were never removed), not a shot meant to be shared.
    // Letting those through leaks another colour's photography into every
    // colourway, which is worse than showing one plate fewer.
    const matching = colour
      ? this.#allCells.filter((cell) => this.#groupColours[cell.dataset.group] === colour)
      : this.#allCells;

    // A colour with no plates of its own (photography still pending) falls
    // back to the full set rather than to an empty wall.
    this.#applyLimit(matching.length ? matching : this.#allCells);

    this.refs.wall.scrollTo({ left: 0 });
    this.#syncWall();
    this.#syncRailEnds();
  }

  #applyLimit(cells) {
    const limit = Number(this.refs.wall.dataset.limit) || 0;
    const visible = limit > 0 ? cells.slice(0, limit) : cells;
    const shown = new Set(visible);

    // Newly revealed plates are still lazy, so the wall would flash empty for
    // a beat on a colour switch. Promote them before unhiding.
    for (const cell of visible) {
      const img = cell.querySelector('img');
      if (img?.loading === 'lazy') img.loading = 'eager';
    }

    for (const cell of this.#allCells) cell.hidden = !shown.has(cell);

    // The lightbox pages through what is on the wall, so re-index as we go.
    this.#cells = visible;
    visible.forEach((cell, index) => {
      cell.dataset.index = String(index);
    });
  }

  /* ---------- sticky wall ---------- */

  #syncWall = () => {
    if (window.innerWidth <= 1080) {
      this.style.top = '';
      return;
    }

    const header = this.#headerOffset();
    const height = this.offsetHeight;
    const viewport = window.innerHeight;

    this.style.top = height + header > viewport ? `${viewport - height - 24}px` : `${header}px`;
  };

  #headerOffset() {
    const styles = getComputedStyle(document.documentElement);
    const measured =
      parseFloat(styles.getPropertyValue('--sticky-header-offset')) ||
      parseFloat(styles.getPropertyValue('--header-group-height')) ||
      0;

    if (Number.isFinite(measured) && measured > 0) return measured;

    // Both variables can still be empty here — they are only written when
    // something on the page asks the theme to measure, and a cached render
    // can beat that. Fall back to the header group's live box so the pin
    // never lands underneath the chrome.
    const group = document.querySelector('#header-group');
    return group instanceof HTMLElement ? group.getBoundingClientRect().height : 0;
  }

  /* ---------- rail ---------- */

  #railStep(direction) {
    const [cell] = this.#cells;
    if (!cell) return;

    const gap = parseFloat(getComputedStyle(this.refs.wall).columnGap) || 0;
    this.refs.wall.scrollBy({
      left: direction * (cell.getBoundingClientRect().width + gap),
      behavior: 'smooth',
    });
  }

  #syncRailEnds = () => {
    const { wall, railPrev, railNext } = this.refs;
    if (!railPrev || !railNext) return;

    railPrev.disabled = wall.scrollLeft < 4;
    railNext.disabled = wall.scrollLeft > wall.scrollWidth - wall.clientWidth - 4;
  };

  /* ---------- lightbox ---------- */

  #openFromCell = (event) => {
    const cell = event.target.closest('.it-gallery__cell');
    if (!cell) return;

    this.#showShot(Number(cell.dataset.index) || 0);
    this.refs.lightbox.hidden = false;

    // Lift the gallery's own stacking context over the sticky header for as
    // long as the overlay is up — see .is-zoomed in the block's sheet.
    this.classList.add('is-zoomed');

    // A forced reflow, not requestAnimationFrame: rAF never fires in a
    // background tab or an offscreen render, and an overlay that never gets
    // .is-open stays at opacity 0 with the page locked behind it.
    void this.refs.lightbox.offsetHeight;
    this.refs.lightbox.classList.add('is-open');

    // On desktop the scroller is .page-wrapper, not the document, so
    // `body { overflow: hidden }` left the page scrolling behind the overlay.
    // The theme's own lock drives `html[scroll-lock]`, which covers both.
    lockScroll(this);
    this.refs.lightboxClose?.focus();
  };

  #showShot(index) {
    const total = this.#cells.length;
    if (!total) return;

    this.#lightboxIndex = (index + total) % total;

    const cell = this.#cells[this.#lightboxIndex];
    const img = cell?.querySelector('img');

    this.refs.lightboxImage.src = cell?.dataset.full || img?.currentSrc || img?.src || '';
    this.refs.lightboxImage.alt = img?.alt || '';
    this.refs.lightboxCount.textContent = `${String(this.#lightboxIndex + 1).padStart(2, '0')} / ${String(
      total
    ).padStart(2, '0')}`;
  }

  #closeLightbox = () => {
    this.refs.lightbox.classList.remove('is-open');
    unlockScroll(this);
    // Wait out the fade before pulling it from the a11y tree and dropping the
    // gallery back below the header.
    setTimeout(() => {
      this.refs.lightbox.hidden = true;
      this.classList.remove('is-zoomed');
    }, 300);
  };

  #onKeydown = (event) => {
    if (this.refs.lightbox?.hidden !== false) return;

    if (event.key === 'Escape') this.#closeLightbox();
    if (event.key === 'ArrowLeft') this.#showShot(this.#lightboxIndex - 1);
    if (event.key === 'ArrowRight') this.#showShot(this.#lightboxIndex + 1);
  };

  /* ---------- tools ---------- */

  #share = async () => {
    const url = new URL(this.dataset.productUrl || window.location.pathname, window.location.origin).href;

    if (navigator.share) {
      try {
        await navigator.share({ title: document.title, url });
        return;
      } catch {
        // The customer dismissed the sheet — fall through to copy.
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      this.dispatchEvent(new CustomEvent('it:toast', { bubbles: true, detail: { message: 'Link copied' } }));
    } catch {
      // Clipboard blocked (insecure context or denied permission) — nothing
      // useful left to try, and a thrown error here would be noise.
    }
  };
}

if (!customElements.get('it-pdp-gallery')) {
  customElements.define('it-pdp-gallery', ItPdpGallery);
}
